import { all, call, put, SagaReturnType, takeLatest } from 'redux-saga/effects';

import * as episodeDetailsActions from '@/admin/pages/studyGoEpisodeDetails/Actions';
import { episodeDetailsActionTypes } from '@/admin/pages/studyGoEpisodeDetails/Constants';
import { processRequest } from '@/services/Api';

import type { Episode, Hint, Room } from '@/admin/pages/studyGoBridgeEpisodes/types';
import type {
	AddHintRequestAction,
	AddRoomRequestAction,
	GetEpisodeDetailsRequestAction,
	UpdateHintRequestAction,
	UpdateRoomRequestAction,
} from '@/admin/pages/studyGoEpisodeDetails/Actions';

interface EpisodeDetailsApiResponse {
	data: {
		study_go_bridge_episode: Episode;
	};
}

export function* handleGetEpisodeDetailsRequest(action: GetEpisodeDetailsRequestAction) {
	try {
		const { episodeId } = action.payload;
		const response: SagaReturnType<typeof processRequest> = yield call(
			processRequest,
			`admin/study_go_bridge/episodes/${episodeId}`,
			'GET',
		);

		const { study_go_bridge_episode } = response.data as EpisodeDetailsApiResponse['data'];

		yield put(
			episodeDetailsActions.getEpisodeDetailsSuccess({ episode: study_go_bridge_episode }),
		);
	} catch (error) {
		const errorMessage =
			error instanceof Error ? error.message : 'Failed to load episode details';
		yield put(episodeDetailsActions.getEpisodeDetailsError(errorMessage));
	}
}

interface AddRoomApiResponse {
	data: {
		room: Room;
	};
}

export function* handleAddRoomRequest(action: AddRoomRequestAction) {
	try {
		const { episodeId, roomData } = action.payload;
		const response: SagaReturnType<typeof processRequest> = yield call(
			processRequest,
			`admin/study_go_bridge/episodes/${episodeId}/rooms`,
			'POST',
			{ study_go_bridge_room: roomData },
		);

		const addedRoom = response.data as AddRoomApiResponse['data'];

		yield put(episodeDetailsActions.addRoomSuccess(addedRoom));
		// Reload episode details to get updated rooms list
		yield put(episodeDetailsActions.getEpisodeDetailsRequest(episodeId));
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Failed to add room';
		yield put(episodeDetailsActions.addRoomError(errorMessage));
	}
}

interface AddHintApiResponse {
	data: {
		hint: Hint;
	};
}

export function* handleAddHintRequest(action: AddHintRequestAction) {
	try {
		const { episodeId, hintData } = action.payload;
		const response: SagaReturnType<typeof processRequest> = yield call(
			processRequest,
			`admin/study_go_bridge/episodes/${episodeId}/episode_hints`,
			'POST',
			{ study_go_bridge_episode_hint: hintData },
		);

		const addedHint = response.data as AddHintApiResponse['data'];

		yield put(episodeDetailsActions.addHintSuccess(addedHint));
		// Reload episode details to get updated hints list
		yield put(episodeDetailsActions.getEpisodeDetailsRequest(episodeId));
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Failed to add episode hint';
		yield put(episodeDetailsActions.addHintError(errorMessage));
	}
}

interface UpdateRoomApiResponse {
	data: {
		room: Room;
	};
}

export function* handleUpdateRoomRequest(action: UpdateRoomRequestAction) {
	try {
		const { episodeId, roomId, roomData } = action.payload;
		const response: SagaReturnType<typeof processRequest> = yield call(
			processRequest,
			`admin/study_go_bridge/episodes/${episodeId}/rooms/${roomId}`,
			'PUT',
			{ study_go_bridge_room: roomData },
		);

		const updatedRoom = response.data as UpdateRoomApiResponse['data'];

		yield put(episodeDetailsActions.updateRoomSuccess(updatedRoom));
		// Reload episode details to get updated rooms list
		yield put(episodeDetailsActions.getEpisodeDetailsRequest(episodeId));
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Failed to update room';
		yield put(episodeDetailsActions.updateRoomError(errorMessage));
	}
}

interface UpdateHintApiResponse {
	data: {
		hint: Hint;
	};
}

export function* handleUpdateHintRequest(action: UpdateHintRequestAction) {
	try {
		const { episodeId, hintId, hintData } = action.payload;
		const response: SagaReturnType<typeof processRequest> = yield call(
			processRequest,
			`admin/study_go_bridge/episodes/${episodeId}/episode_hints/${hintId}`,
			'PUT',
			{ study_go_bridge_episode_hint: hintData },
		);

		const updatedHint = response.data as UpdateHintApiResponse['data'];

		yield put(episodeDetailsActions.updateHintSuccess(updatedHint));
		// Reload episode details to get updated hints list
		yield put(episodeDetailsActions.getEpisodeDetailsRequest(episodeId));
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Failed to update hint';
		yield put(episodeDetailsActions.updateHintError(errorMessage));
	}
}

export default function* episodeDetailsSagas(): Generator {
	yield all([
		yield takeLatest(
			episodeDetailsActionTypes.GET_EPISODE_DETAILS_REQUEST,
			handleGetEpisodeDetailsRequest,
		),
		yield takeLatest(episodeDetailsActionTypes.ADD_ROOM_REQUEST, handleAddRoomRequest),
		yield takeLatest(episodeDetailsActionTypes.ADD_HINT_REQUEST, handleAddHintRequest),
		yield takeLatest(episodeDetailsActionTypes.UPDATE_ROOM_REQUEST, handleUpdateRoomRequest),
		yield takeLatest(episodeDetailsActionTypes.UPDATE_HINT_REQUEST, handleUpdateHintRequest),
	]);
}
